import Card from "@material-ui/core/Card";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { RefObject, useEffect, useRef, useState } from "react";
import {
    changeAppState,
    getAppState,
    useAppState,
} from "../../../../common/appState";
import { adminUserId } from "../../../../common/consts";
import { useScreenSize } from "../../../../common/hooks/useScreenSize";
import { useUser } from "../../../../common/hooks/useUser";
import { Quest } from "../../../zApps/Layout/Login/MyPage/components/OpenableCards/QuestsCard/quests/types";
import { getLinkText } from "../../../zApps/Layout/Login/MyPage/Fields/BioField/BioLinks/getLinkText";
import { ShareButton } from "../../../zApps/Layout/Login/MyPage/MyPageTop/components/ShareButton";
import { countryNames } from "../../County/countryCodeAndName";
import { CountryFlag } from "../../County/CountryFlag";
import { Markdown } from "../../Markdown";
import { RightPanel } from "../../Panel/RightPanel";
import { Post } from "../../Panel/Social/Panel/types";
import { ScrollBox } from "../../ScrollBox";
import { OtherUser, User, UserForRanking } from "../types";
import { FriendList } from "./components/FriendList";
import { AchievementsCard } from "./components/LowerArea/AchievementsCard";
import { SocialCard } from "./components/LowerArea/SocialCard";
import { NameAndLevel } from "./components/NameAndLevel";
import { PcContent } from "./components/PcContent";
import { InfoForAdmin } from "./components/PrivateLessonInfoForAdmin";
import { SmartPhoneContent } from "./components/SmartPhoneContent";
import { getUserStudyAchievements } from "./getUserStudyAchievements";
import { OtherUserPanelState } from "./types";

export default function OtherUserPanels() {
    const [otherUserPanelsState, setOtherUserPanelsState] = useAppState(
        "otherUserPanelsState"
    );
    const { user, isUserFetchDone } = useUser();

    return (
        <>
            {otherUserPanelsState.map(state => (
                <OtherUserPanel
                    key={state.targetUserId}
                    otherUserPanelState={state}
                    setOtherUserPanelState={newState => {
                        if (
                            otherUserPanelsState.some(
                                s => s.targetUserId === newState.targetUserId
                            )
                        ) {
                            setOtherUserPanelsState(
                                otherUserPanelsState.map(s => {
                                    if (
                                        s.targetUserId === newState.targetUserId
                                    ) {
                                        return newState;
                                    }
                                    return s;
                                })
                            );
                            return;
                        }
                        setOtherUserPanelsState([
                            ...otherUserPanelsState,
                            newState,
                        ]);
                    }}
                    user={user}
                    isUserFetchDone={isUserFetchDone}
                />
            ))}
        </>
    );
}

function OtherUserPanel({
    otherUserPanelState,
    setOtherUserPanelState,
    user,
    isUserFetchDone,
}: {
    otherUserPanelState: OtherUserPanelState;
    setOtherUserPanelState: (value: OtherUserPanelState) => void;
    user?: User;
    isUserFetchDone: boolean;
}) {
    const [_targetUser, setTargetUser] = useState<OtherUser | undefined>(
        undefined
    );
    const [studyAchievements, setStudyAchievements] = useState<Quest[]>([]);
    const [friends, setFriends] = useState<UserForRanking[]>([]);

    useEffect(() => {
        (async () => {
            if (!otherUserPanelState.open) {
                const id = setTimeout(() => {
                    setTargetUser(undefined);
                    setStudyAchievements([]);
                }, 500);
                return () => {
                    clearTimeout(id);
                };
            }

            try {
                // OtherUser基本情報のFetch
                setTargetUser(
                    await (
                        await fetch(
                            "api/User/GetOtherUserInfo?userId=" +
                                otherUserPanelState.targetUserId
                        )
                    ).json()
                );
                // AchievementsのFetch
                setStudyAchievements(
                    getUserStudyAchievements(
                        await (
                            await fetch(
                                "api/User/GetAchievements?userId=" +
                                    otherUserPanelState.targetUserId
                            )
                        ).json()
                    )
                );
                // FriendsのFetch
                setFriends(
                    await (
                        await fetch(
                            "api/User/GetAllFriends?userId=" +
                                otherUserPanelState.targetUserId
                        )
                    ).json()
                );
            } catch {
                alert("User not found");
                changeAppState(
                    "otherUserPanelsState",
                    getAppState().otherUserPanelsState.filter(
                        s => s.targetUserId !== otherUserPanelState.targetUserId
                    )
                );
            }
        })();
    }, [otherUserPanelState]);

    const isMyself = !!user && user?.userId === _targetUser?.userId;
    const targetUser = isMyself
        ? {
              ...user,
              requestFromMe: null,
              requestFromThem: null,
              isFriend: false,
          }
        : _targetUser;

    const isOpen = otherUserPanelState.open;

    const scrollableContainerRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (otherUserPanelState.scrollToTop) {
            scrollableContainerRef.current?.scrollTo({
                top: 0,
                behavior: "smooth",
            });
            setOtherUserPanelState({
                ...otherUserPanelState,
                scrollToTop: false,
            });
        }
    }, [otherUserPanelState, setOtherUserPanelState]);

    const closePanel = () => {
        setOtherUserPanelState({
            targetUserId: targetUser?.userId ?? 0,
            open: false,
        });
    };

    return (
        <RightPanel
            panelName={
                targetUser
                    ? `OtherUserPanel-${targetUser.name}(${targetUser?.userId})`
                    : undefined
            }
            open={isOpen}
            onClose={closePanel}
            panelWidth={1000}
            globalPanelState={otherUserPanelState}
            isChildrenUnmounted={
                !targetUser ||
                !isOpen ||
                otherUserPanelState.targetUserId !== targetUser.userId
            }
            style={{ paddingRight: 5, paddingLeft: 5 }}
            topItem={<ShareButton userId={targetUser?.userId} />}
            scrollableContainerRef={scrollableContainerRef}
        >
            {targetUser && (
                <OtherUserArea
                    targetUser={targetUser}
                    isMyself={isMyself}
                    studyAchievements={studyAchievements}
                    user={user}
                    isUserFetchDone={isUserFetchDone}
                    scrollableContainerRef={scrollableContainerRef}
                    setTargetUser={setTargetUser}
                    friends={friends}
                />
            )}
        </RightPanel>
    );
}

function OtherUserArea({
    targetUser,
    isMyself,
    studyAchievements,
    user,
    isUserFetchDone,
    scrollableContainerRef,
    setTargetUser,
    friends,
}: {
    targetUser: OtherUser;
    isMyself: boolean;
    studyAchievements: Quest[];
    user?: User;
    isUserFetchDone: boolean;
    scrollableContainerRef: RefObject<HTMLDivElement>;
    setTargetUser: (targetUser: OtherUser) => void;
    friends: UserForRanking[];
}) {
    const { screenWidth, screenHeight } = useScreenSize();

    const isCommentUsed = screenWidth > 767;
    const isVeryNarrow = screenWidth < 500;
    const imageSrc = targetUser.avatarPath
        ? targetUser.avatarPath.replace("150_150", "width300")
        : "";

    const [posts, setPosts] = useState<Post[]>([]);
    const [minTime, setMinTime] = useState("");
    const fetchedPosts = minTime !== "";
    const noPosts = posts.length < 1;
    const [shouldMountSocial, setShouldMountSocial] = useState(true);
    useEffect(() => {
        if (fetchedPosts && noPosts) {
            setShouldMountSocial(false);
        }
    }, [fetchedPosts, noPosts]);

    const noAchievements = studyAchievements.length < 1;
    const noFriends = friends.length < 1;
    const c = useAuthorAreaStyles({ isCommentUsed });

    const flagContainerRef = useRef<HTMLDivElement>(null);

    const achievements = (
        <AchievementsCard studyAchievements={studyAchievements} />
    );
    const friendList = (
        <FriendList
            users={friends}
            pcScrollableContainerRef={scrollableContainerRef}
            isPcMode={isCommentUsed}
            screenHeight={screenHeight}
        />
    );
    const socialCardRef = useRef<HTMLDivElement>(null);
    const social = (
        <SocialCard
            user={user}
            isUserFetchDone={isUserFetchDone}
            scrollableContainerRef={scrollableContainerRef}
            targetUserId={targetUser.userId}
            posts={posts}
            setPosts={setPosts}
            minTime={minTime}
            setMinTime={setMinTime}
            socialCardRef={socialCardRef}
            checkFetchNecessary={() => {
                if (!isCommentUsed) {
                    return false;
                }
                const socialCardEl = socialCardRef.current;
                if (!socialCardEl) {
                    return false;
                }
                const socialCardTop =
                    socialCardEl.getBoundingClientRect()?.top ?? 0;
                const socialCardHeight = socialCardEl.clientHeight;

                if (socialCardTop + socialCardHeight - screenHeight > 1000) {
                    // まだSocialカードの下部が、見えている範囲の一番下に近づいていない
                    return false;
                }
                return true; // 右要素（AchievementsやFriends）の方が高かったら、スクロールが下の方まで行っていなくても追加ロードが必要な可能性があるため、この関数の判定結果を優先してリロード要求
            }}
        />
    );

    return (
        <>
            <ScrollBox style={{ textAlign: "center" }}>
                <NameAndLevel targetUser={targetUser} />

                {targetUser.countryCode && (
                    <div
                        className={c.countryArea}
                        onClick={() => {
                            if (!targetUser.countryCode) {
                                return;
                            }
                            window.open(
                                `https://maps.google.com/maps?q=${countryNames[
                                    targetUser.countryCode
                                ].replaceAll(" ", "+")}`
                            );
                        }}
                        ref={flagContainerRef}
                    >
                        <CountryFlag
                            code={targetUser.countryCode}
                            containerRef={flagContainerRef}
                        />
                        <span className={c.countryName}>
                            {countryNames[targetUser.countryCode]}
                        </span>
                    </div>
                )}

                {isCommentUsed ? (
                    <PcContent
                        targetUser={targetUser}
                        imageSrc={imageSrc}
                        screenWidth={screenWidth}
                        comment={
                            <div className={c.markdownContainer}>
                                <Markdown
                                    style={{
                                        margin: 5,
                                        textAlign: "left",
                                        fontSize: "large",
                                    }}
                                    source={getBioWithLinks(targetUser)}
                                />
                            </div>
                        }
                        isMyself={isMyself}
                    />
                ) : (
                    <SmartPhoneContent
                        targetUser={targetUser}
                        imageSrc={imageSrc}
                        isMyself={isMyself}
                        isVeryNarrow={isVeryNarrow}
                    />
                )}

                {user?.userId === adminUserId && (
                    <InfoForAdmin
                        targetUser={targetUser}
                        setTargetUser={setTargetUser}
                    />
                )}

                {(() => {
                    if (isCommentUsed && shouldMountSocial) {
                        // PCかつ、左側にSocialを出せる
                        return (
                            <>
                                <hr className={c.hr} />
                                <div className={c.flex}>
                                    <div className={c.width50p}>
                                        <Card>{social}</Card>
                                    </div>
                                    <div className={c.width50p}>
                                        {(!noAchievements || !noFriends) && (
                                            <div
                                                className={
                                                    c.pcAchievementsAndFriendsContainer
                                                }
                                            >
                                                {!noAchievements &&
                                                    achievements}
                                                {!noFriends && friendList}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        );
                    }
                    if (isCommentUsed && !(noAchievements || noFriends)) {
                        // PCだが、左側にSocialを出せない
                        // しかし、AchievementsとFriendsは両方出せる
                        return (
                            <>
                                <hr className={c.hr} />
                                <div className={c.flex}>
                                    <div className={c.width50p}>
                                        <Card>{achievements}</Card>
                                    </div>
                                    <div className={c.width50p}>
                                        {friendList}
                                    </div>
                                </div>
                            </>
                        );
                    }
                    if (isCommentUsed && !(noAchievements && noFriends)) {
                        // PCだが、左側にSocialを出せない
                        // しかし、AchievementsかFriendsのどちらかは出せる
                        return (
                            <>
                                <hr className={c.hr} />
                                <div className={c.flex}>
                                    <div className={c.width50p}>
                                        {!noAchievements && achievements}
                                        {!noFriends && friendList}
                                    </div>
                                    <div className={c.width50p}></div>
                                </div>
                            </>
                        );
                    }
                    if (isCommentUsed) {
                        // PCにおいて、下部に出すコンテンツが何もない
                        return null;
                    }
                    if (noAchievements) {
                        // SPにおいて、巻物内の下部に出すコンテンツが何もない
                        // （スマホではAchievements以外は巻物の外）
                        return null;
                    }
                    return (
                        <>
                            <hr className={c.hr} />
                            {achievements}
                        </>
                    );
                })()}
            </ScrollBox>

            {(() => {
                if (isCommentUsed) {
                    return null; // PCでは巻物の下に何も出さない
                }
                if (!shouldMountSocial && noFriends) {
                    return null; // SPにおいて、巻物の下に出すものが何もない
                }
                return (
                    <div className={c.spFriendsAndSocialContainer}>
                        {!noFriends && (
                            <div className={c.spFriendsContainer}>
                                {friendList}
                            </div>
                        )}
                        {shouldMountSocial && (
                            <div className={c.bottom10}>{social}</div>
                        )}
                    </div>
                );
            })()}
        </>
    );
}
const useAuthorAreaStyles = makeStyles<Theme, { isCommentUsed: boolean }>(
    theme => ({
        markdownContainer: { margin: 10, "& li": { lineHeight: 2 } },
        hr: ({ isCommentUsed }) => ({
            margin: isCommentUsed ? "24px 0" : "14px 0",
        }),
        flex: { display: "flex", gap: 5, alignItems: "flex-start" },
        pcAchievementsAndFriendsContainer: {
            display: "flex",
            flexDirection: "column",
            gap: 25,
        },
        spFriendsAndSocialContainer: {
            display: "flex",
            flexDirection: "column",
        },
        spFriendsContainer: { paddingTop: 10, marginBottom: 35 },
        width50p: { width: "50%" },
        bottom10: { marginBottom: 10 },
        countryArea: {
            fontSize: "medium",
            position: "relative",
            bottom: 10,
            marginBottom: 10,
            cursor: "pointer",
            marginTop: 5,
        },
        countryName: {
            marginLeft: 5,
            color: theme.palette.primary.main,
            textDecoration: "underline",
        },
    })
);

function getBioWithLinks(targetUser: OtherUser) {
    if (targetUser.bioLinks.length === 0) {
        return targetUser.bio;
    }

    return `${targetUser.bio}

---

${targetUser.bioLinks
    .map(
        l => `- [**${getLinkText(l.title, l.url)}**](${l.url})
`
    )
    .join("")}`;
}
